import { useEffect, useState, type RefObject } from 'react'

export interface FixerRainScrollState {
  scrollTop: number
  /** 0 at rest, 1 once user has scrolled into the hero */
  intensity: number
  /** 0 = rain anchored on subject, 1 = full-width pour into page */
  expand: number
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value))
}

export function useFixerRainScroll(
  scrollRootRef: RefObject<HTMLElement | null>,
  heroPanelRef: RefObject<HTMLElement | null>
): FixerRainScrollState {
  const [state, setState] = useState<FixerRainScrollState>({
    scrollTop: 0,
    intensity: 0,
    expand: 0,
  })

  useEffect(() => {
    const root = scrollRootRef.current
    const hero = heroPanelRef.current
    if (!root || !hero) return

    let raf = 0

    const update = () => {
      const scrollTop = root.scrollTop
      const heroHeight = hero.offsetHeight || 1
      const revealStart = 28
      const revealSpan = heroHeight * 0.2
      const intensity = clamp01((scrollTop - revealStart) / revealSpan)
      const expand = clamp01(scrollTop / (heroHeight * 0.72))

      setState({ scrollTop, intensity, expand })
    }

    const onScroll = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(update)
    }

    update()
    root.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll, { passive: true })

    return () => {
      cancelAnimationFrame(raf)
      root.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [scrollRootRef, heroPanelRef])

  return state
}
